import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { useAsync } from 'react-use'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { Dialog } from '@getgo/chameleon-web-react-wrapper'
import { makeStyles } from '@material-ui/core'
import useLocalStorage from '@rehooks/local-storage'

import { useConnectedUserContext } from 'components/contexts/ConnectedUserContext'
import { AppThemeContext } from 'components/providers/AppThemeProvider'
import { useLocale } from 'hooks'
import { useUserHasAdminRoles } from 'hooks'
import {
  GTATourLengthByRole,
  SHOW_ADMIN_TOUR_FIRST_TIME,
  SHOW_ADMIN_TOUR_POPOVER,
  SHOW_GOTO_ADMIN_TOUR,
} from 'utils/constants'
import fetchMeService from 'utils/fetchMeService'
import getGotoAdminTourData from './GotoAdminTourInfo'
const useStyles = makeStyles(() => ({
  gotoAdminTour: {
    padding: '0.5rem',
    width: '640px',
    ['&::part(overlay)']: {
      zIndex: 1300,
    },
  },
  gotoAdminTourDots: {
    display: 'flex',
    justifyContent: 'center',
    width: '50%',
    gap: '10px',
  },
  gotoAdminTourIcons: {
    display: 'flex',
    flexWrap: 'nowrap',
    padding: 'var(--Spacing-Spacing-scale-spacing-02, 8px) 16px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '13px',
  },
  gotoAdminTourImage: {
    margin: '1rem 0 1.5rem',
    backgroundColor: tokens.surfaceColorBrandSecondary,
    display: 'flex',
    justifyContent: 'center',
  },
  gotoAdminTourCtas: {
    width: '100%',
    margin: '1.5rem 0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    alignItems: 'center',
  },
}))

export interface GotoAdminTourData {
  title: string
  message: JSX.Element
  cta: JSX.Element
}

export const GotoAdminTour: FC = () => {
  const { chameleonTheme } = useContext(AppThemeContext)
  const t = useTranslate()
  const language = useLocale() || 'en_US'
  const classes = useStyles()
  const [screenNumber, setScreenNumber] = useState<number>(0)
  const [isDialogOpened, setDialogOpened] = useState<boolean>(false)
  const [showAdminTour, setShowAdminTour] = useLocalStorage<boolean>(SHOW_GOTO_ADMIN_TOUR)
  const [, setAdminTourPopOver] = useLocalStorage(SHOW_ADMIN_TOUR_POPOVER, false)
  const [showAdminTourFirstTime, SetAdminTourFirstTime] = useLocalStorage<boolean>(SHOW_ADMIN_TOUR_FIRST_TIME)
  const iAm = useConnectedUserContext()
  const userHasAdminRoles = useUserHasAdminRoles()
  const isBillingAdmin = useMemo(
    () => userHasAdminRoles('BILLING_ADMIN', iAm?.accountKey),
    [iAm?.accountKey, userHasAdminRoles],
  )

  const [gotoAdminTourData, setGotoAdminTourData] = useState<GotoAdminTourData>(
    getGotoAdminTourData(t, classes, language, chameleonTheme, screenNumber, setScreenNumber, isBillingAdmin),
  )
  const { loading: loadingAccountFlags } = useAsync(async () => {
    const response = await fetchMeService(
      JSON.stringify({
        query: '{accounts{settings{admin{ goToWalkthroughStep}}}}',
      }),
      'POST',
    )

    if (response) {
      /*
       *if more than one account present then if every account has gotoWalkThroughStep as null then no account has visited gotoAdmin tour
       *if one of the account has gotoWalkThroughStep as not null then show the highest step visited considering all the accounts
       *if there is only one account then show tour depends on single account
       */
      const data: any = response.data
      const accounts = data.accounts
      let goToWalkthroughStep

      if (accounts != null && accounts.length > 1) {
        const isEveryAccountStepNull = accounts.every((account) => account.settings.admin.goToWalkthroughStep === null)
        const max = Math.max(...accounts.map((account) => account.settings.admin.goToWalkthroughStep))
        goToWalkthroughStep = isEveryAccountStepNull ? null : max
      } else {
        goToWalkthroughStep = accounts![0].settings.admin.goToWalkthroughStep
      }

      const res = goToWalkthroughStep === null ? 0 : goToWalkthroughStep
      if (goToWalkthroughStep == null) {
        SetAdminTourFirstTime(true)
        setDialogOpened(true)
      } else {
        SetAdminTourFirstTime(false)
      }
      setScreenNumber(res)
    }
  }, [])

  const handleOnClose = async () => {
    if (showAdminTourFirstTime) {
      SetAdminTourFirstTime(false)
      setAdminTourPopOver(true)
    }
    //if screen number reaches limit then set proper screennumber
    const tourLimit = isBillingAdmin
      ? GTATourLengthByRole.PRODUCT_BILLING_ADMIN_TOUR_MAX_LENGTH
      : GTATourLengthByRole.PRODUCT_ADMIN_TOUR_MAX_LENGTH
    if (screenNumber >= tourLimit || screenNumber == -1) {
      setScreenNumber(0)
    }
    setShowAdminTour(false)
    setDialogOpened(false)

    await fetchMeService(
      JSON.stringify({
        query:
          'mutation{updateAccountSettings(accountKey:' +
          iAm?.accountKey +
          ',admin:{goToWalkthroughStep:' +
          (screenNumber < 0 || screenNumber == tourLimit ? 0 : screenNumber) +
          '})}',
      }),
      'POST',
    )
  }

  useEffect(() => {
    const gotoAdminTourData = getGotoAdminTourData(
      t,
      classes,
      language,
      chameleonTheme,
      screenNumber,
      setScreenNumber,
      isBillingAdmin,
    )
    if (!gotoAdminTourData) {
      setDialogOpened(false)
      handleOnClose()
      return
    }
    if (showAdminTour || showAdminTourFirstTime) {
      setDialogOpened(true)
      setGotoAdminTourData(gotoAdminTourData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenNumber, chameleonTheme, language, showAdminTour, showAdminTourFirstTime, classes])
  return (
    <Dialog
      open={!loadingAccountFlags && isDialogOpened}
      closable
      onClosed={handleOnClose}
      size="large"
      className={classes.gotoAdminTour}
      actions={gotoAdminTourData?.cta}
      title={gotoAdminTourData?.title}
      data-testid="gotoAdminDialog"
    >
      {gotoAdminTourData?.message}
    </Dialog>
  )
}

export default GotoAdminTour
